import { useCallback, useState } from "react";
import type { LegendPayload } from "recharts";

/** Legend styling shared by every chart whose series can be toggled by clicking their legend entry. */
export const toggleableLegendStyle: React.CSSProperties = { cursor: "pointer" };

/**
 * Tracks which series of a chart the user has hidden by clicking its legend entry.
 *
 * Pass `onLegendClick` to a Recharts `<Legend onClick>` and `isHidden(key)` to each series'
 * `hide` prop (or, for a pie, use it to zero out a slice). Cartesian series are keyed by their
 * `dataKey`; pie slices, which share one `dataKey`, are keyed by their legend label (`value`).
 */
export function useSeriesVisibility(): {
  isHidden: (key: string) => boolean;
  toggle: (key: string) => void;
  onLegendClick: (entry: LegendPayload) => void;
  onPieLegendClick: (entry: LegendPayload) => void;
} {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = useCallback((key: string) => {
    setHidden((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const isHidden = useCallback((key: string) => hidden.has(key), [hidden]);

  const onLegendClick = useCallback(
    (entry: LegendPayload) => {
      if (typeof entry.dataKey === "string" || typeof entry.dataKey === "number") {
        toggle(String(entry.dataKey));
      }
    },
    [toggle],
  );

  const onPieLegendClick = useCallback(
    (entry: LegendPayload) => {
      if (typeof entry.value === "string") {
        toggle(entry.value);
      }
    },
    [toggle],
  );

  return { isHidden, toggle, onLegendClick, onPieLegendClick };
}
